// Phone contacts: every person on the file with tap-to-call and tap-to-email.
import { useEffect, useState } from "react";
import { Mail, Phone, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getAgeingLoanParties } from "@/lib/api";

export function PhoneContacts({ contacts, loading, onCall, onEmail, caseId }) {
  if (loading) return <div className="py-10 text-center text-sm text-slate-500">Loading contacts…</div>;
  return (
    <>
      {caseId && <PhoneLoanParties caseId={caseId} onCall={onCall} onEmail={onEmail} />}
      <PhoneContactList contacts={contacts} onCall={onCall} onEmail={onEmail} />
    </>
  );
}

const PARTY_PILL = { primary: "bg-blue-50 text-blue-800 border-blue-200", co_borrower: "bg-emerald-50 text-emerald-800 border-emerald-200", ld_guarantor_record: "bg-amber-50 text-amber-800 border-amber-200" };
const PARTY_LABEL = { primary: "Primary borrower", co_borrower: "Co-borrower", ld_guarantor_record: "LoanDisk guarantor record", portal_contact: "Portal only" };

function PhoneLoanParties({ caseId, onCall, onEmail }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let off = false;
    getAgeingLoanParties(caseId).then((r) => { if (!off) setData(r); }).catch((e) => { if (!off) setErr(e?.response?.data?.detail || e.message); });
    return () => { off = true; };
  }, [caseId]);
  const parties = data?.parties || [];
  const onLoan = parties.filter((p) => p.on_loan).length;
  return (
    <div className="bg-white border-b" data-testid="phone-loan-parties">
      <div className="px-3 pt-2.5 pb-1 text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" /> Borrowers on this loan
        {data && <span className="normal-case tracking-normal text-slate-400" data-testid="phone-loan-parties-summary">· {onLoan} on loan, {parties.length - onLoan} not attached</span>}
      </div>
      {!data && !err && <div className="px-3 pb-2 text-xs text-slate-500" data-testid="phone-loan-parties-loading">Reading the loan from LoanDisk…</div>}
      {err && <div className="px-3 pb-2 text-xs text-amber-700" data-testid="phone-loan-parties-error">{err}</div>}
      {parties.map((p, i) => (
        <div key={`${p.kind}-${p.ld_borrower_id || i}`} className="px-3 py-2 flex items-center gap-3 border-t" data-testid="phone-loan-party">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[15px] font-medium truncate">{p.name}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded border ${PARTY_PILL[p.kind] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{PARTY_LABEL[p.kind] || p.role}{p.share_percent ? ` ${p.share_percent}%` : ""}</span>
              {!p.on_loan && <span className="text-[9px] text-slate-400">not on LoanDisk loan</span>}
            </div>
            {p.business_name && <div className="text-xs text-slate-600 truncate">{p.business_name}</div>}
            {p.mobile && <div className="text-xs font-mono text-slate-600 truncate">{p.mobile}</div>}
            {p.email && <div className="text-xs text-slate-600 truncate">{p.email}</div>}
            {p.address && <div className="text-[11px] text-slate-500 break-words">{p.address}</div>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {p.mobile && <button type="button" onClick={() => onCall(p.mobile)} className="h-11 w-11 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center" aria-label={`Call ${p.name}`} data-testid="phone-loan-party-call"><Phone className="w-5 h-5" /></button>}
            {p.email && <button type="button" onClick={() => onEmail(p.email)} className="h-11 w-11 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center" aria-label={`Email ${p.name}`} data-testid="phone-loan-party-email"><Mail className="w-5 h-5" /></button>}
          </div>
        </div>
      ))}
      {data && parties.some((p) => !p.on_loan && p.can_attach) && <div className="px-3 py-1.5 text-[11px] text-slate-500 border-t">Attach as co-borrower from the desktop file.</div>}
    </div>
  );
}

function PhoneContactList({ contacts, onCall, onEmail }) {
  if (!contacts?.length) return <div className="py-10 text-center text-sm text-slate-500" data-testid="phone-contacts-empty"><Users className="w-8 h-8 mx-auto mb-2 opacity-40" />No contacts on this file yet — add them from a desktop.</div>;
  return (
    <div className="divide-y bg-white" data-testid="phone-contacts">
      {contacts.map((c, i) => (
        <div key={c.id || `${c.name}-${i}`} className="px-3 py-2.5 flex items-center gap-3" data-testid={`phone-contact-${i}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[15px] font-medium truncate">{c.name || "—"}</span>
              {c.role && <Badge variant="outline" className="text-[9px] font-normal">{c.role}</Badge>}
              {c.source && <Badge variant="outline" className="text-[9px] font-normal text-slate-500">{c.source}</Badge>}
            </div>
            {c.phone && <div className="text-xs font-mono text-slate-600 truncate">{c.phone}</div>}
            {c.email && <div className="text-xs text-slate-600 truncate">{c.email}</div>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {c.phone && <button type="button" onClick={() => onCall(c.phone)} className="h-11 w-11 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center sf-tap active:bg-emerald-100" aria-label={`Call ${c.name}`} data-testid={`phone-contact-call-${i}`}><Phone className="w-5 h-5" /></button>}
            {c.email && <button type="button" onClick={() => onEmail(c.email)} className="h-11 w-11 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center sf-tap active:bg-blue-100" aria-label={`Email ${c.name}`} data-testid={`phone-contact-email-${i}`}><Mail className="w-5 h-5" /></button>}
          </div>
        </div>
      ))}
    </div>
  );
}
